/**
 * assets/cart-upsell.js
 *
 * Powers the dynamic "Complete your routine" upsell in the cart drawer.
 * - Reads the current cart (/cart.js)
 * - Bases recommendations of the most recently added line item
 * - Fetches /recommendations/products.json
 * - Skips anything already in the cart
 * - Adds the chosen recommendation via /cart/add.js
 * - Fires a `cart:updated` CustomEvent so your existing drawer refresh
 *   logic can pick it up (adjust the event name below if yours differs)
 */

(function () {
  const CART_UPSELL_CANDIDATE_LIMIT = 2; // cart-drawer upsell only — unrelated to the sitewide "product-recommendations" section limit
  const CART_UPDATED_EVENT = 'theme:cart:reload'; // Pipeline theme's own CartItems class listens for this on `document` and refreshes line items, subtotal, and checkout price

  const upsellRoot = document.querySelector('[data-cart-upsell]');
  if (!upsellRoot) return;

  const productSlot = upsellRoot.querySelector('[data-upsell-product]');

  async function getCart() {
    const res = await fetch('/cart.js');
    return res.json();
  }

  async function getRecommendations(productId) {
    const res = await fetch(
      `/recommendations/products.json?product_id=${productId}&limit=${CART_UPSELL_CANDIDATE_LIMIT}&intent=related`
    );
    if (!res.ok) return [];
    const data = await res.json();
    return data.products || [];
  }

  function normalizeProductName(title) {
    // Strips common "variant as separate product" suffixes so
    // "REN Beauty Brush Pouch in Blush" and "REN Beauty Brush Pouch"
    // are recognized as the same underlying item.
    return title
      .toLowerCase()
      .split(/\s+(?:in|-|–|—)\s+/)[0] // cut at " in ", " - ", " – ", " — "
      .replace(/\(.*?\)/g, '') // drop anything in parens, e.g. "(2.7 oz)"
      .trim();
  }

  function pickRecommendation(recommendations, cartProductIds, cartProductNames) {
    return recommendations.find((p) => {
      if (cartProductIds.has(p.id)) return false;
      if (cartProductNames.has(normalizeProductName(p.title))) return false;
      return true;
    });
  }

  function renderUpsell(product) {
    const initialVariant = product.variants[0];
    const image = product.featured_image || (product.images && product.images[0]);
    const hasMultipleVariants = product.variants.length > 1;
    const hasNamedSingleVariant =
      !hasMultipleVariants && initialVariant.title !== 'Default Title';

    const variantMarkup = hasMultipleVariants
      ? `<select class="cart__upsell__variant" data-upsell-variant>
          ${product.variants
            .map(
              (v) =>
                `<option value="${v.id}" data-price="${v.price}" ${
                  v.id === initialVariant.id ? 'selected' : ''
                }>${v.title}</option>`
            )
            .join('')}
        </select>`
      : hasNamedSingleVariant
      ? `<p class="cart__upsell__variant" style="text-decoration:none;">${initialVariant.title}</p>`
      : '';

    productSlot.innerHTML = `
      ${image ? `<img src="${image}" alt="${product.title}" width="48" height="48">` : ''}
      <div class="cart__upsell__info">
        <p class="cart__upsell__name">${product.title}</p>
        ${variantMarkup}
      </div>
      <button
        type="button"
        class="cart__upsell__add"
        data-upsell-add
        data-variant-id="${initialVariant.id}"
        data-price="${initialVariant.price}"
      >Add — ${formatMoney(initialVariant.price)}</button>
    `;

    upsellRoot.hidden = false;

    const addButton = productSlot.querySelector('[data-upsell-add]');
    addButton.addEventListener('click', handleAddClick);

    const variantSelect = productSlot.querySelector('[data-upsell-variant]');
    if (variantSelect) {
      variantSelect.addEventListener('change', (event) => {
        const selected = event.target.selectedOptions[0];
        addButton.dataset.variantId = selected.value;
        addButton.dataset.price = selected.dataset.price;
        addButton.textContent = `Add — ${formatMoney(selected.dataset.price)}`;
      });
    }
  }

  function formatMoney(cents) {
    // Swap for Shopify.formatMoney(cents, moneyFormat) if you have that
    // helper loaded globally — this is a safe plain fallback.
    return `$${(cents / 100).toFixed(2)}`;
  }

  const CART_LOADER_CLASS = 'cart--loading'; // Pipeline theme's CartItems.lockState() applies this to [data-cart-loading]; its own showForm() removes it once the reload finishes
  const NEW_ROW_FLASH_CLASS = 'cart-upsell__row-flash';
  const NEW_ROW_FLASH_DURATION = 1100; // ms — should roughly match the CSS animation duration below
  const NEW_ROW_WATCH_TIMEOUT = 5000; // stop watching if the row never shows up (failed/slow reload)

  // The added item doesn't exist in the DOM yet when we add it — it only
  // appears once the theme's own reload rebuilds the line items HTML. So we
  // watch for it to land, then flash-highlight that row as a stand-in for
  // the per-row loadbar the theme uses on quantity changes (which needs an
  // existing row to attach to, and this one doesn't exist until after).
  function flashNewCartRow(variantId) {
    const itemsContainer = document.querySelector('[data-line-items]');
    if (!itemsContainer) return;

    const tryFlash = () => {
      const match = itemsContainer.querySelector(`[data-update-cart^="${variantId}:"]`);
      if (!match) return false;

      const row = match.closest('[data-cart-item]') || match.closest('.cart__items__row') || match;
      row.classList.add(NEW_ROW_FLASH_CLASS);
      setTimeout(() => row.classList.remove(NEW_ROW_FLASH_CLASS), NEW_ROW_FLASH_DURATION);
      return true;
    };

    // In case the row is already there by the time we check (unlikely, but cheap to cover)
    if (tryFlash()) return;

    const observer = new MutationObserver(() => {
      if (tryFlash()) observer.disconnect();
    });
    observer.observe(itemsContainer, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), NEW_ROW_WATCH_TIMEOUT);
  }

  async function handleAddClick(event) {
    const button = event.currentTarget;
    const variantId = button.dataset.variantId;
    const priceLabel = `Add — ${formatMoney(button.dataset.price)}`;
    const loader = document.querySelector('[data-cart-loading]');

    button.disabled = true;
    button.textContent = 'Adding…';
    if (loader) loader.classList.add(CART_LOADER_CLASS);

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 }),
      });

      if (!res.ok) throw new Error('Add to cart failed');

      button.dataset.state = 'added';
      button.textContent = 'Added';

      flashNewCartRow(variantId);

      // Let the rest of the theme know the cart changed so the drawer
      // (line items, subtotal, checkout button) can refresh itself.
      document.dispatchEvent(new CustomEvent(CART_UPDATED_EVENT));

      // Refresh the upsell itself so it doesn't keep recommending
      // something that's now in the cart.
      init();
    } catch (err) {
      button.disabled = false;
      button.textContent = priceLabel;
      if (loader) loader.classList.remove(CART_LOADER_CLASS);
      console.error('[cart-upsell]', err);
    }
  }

  async function init() {
    try {
      const cart = await getCart();

      if (!cart.items.length) {
        upsellRoot.hidden = true;
        return;
      }

      const cartProductIds = new Set(cart.items.map((i) => i.product_id));
      const cartProductNames = new Set(
        cart.items.map((i) => normalizeProductName(i.product_title))
      );
      const lastItem = cart.items[cart.items.length - 1];

      const recommendations = await getRecommendations(lastItem.product_id);
      const pick = pickRecommendation(recommendations, cartProductIds, cartProductNames);

      if (!pick) {
        upsellRoot.hidden = true;
        return;
      }

      renderUpsell(pick);
    } catch (err) {
      console.error('[cart-upsell]', err);
      upsellRoot.hidden = true;
    }
  }

  // Run on initial load
  document.addEventListener('DOMContentLoaded', init);

  // Re-run our own upsell whenever the cart changes elsewhere too, so it
  // doesn't keep recommending something that's now in the cart.
  document.addEventListener(CART_UPDATED_EVENT, init);
})();